import React from 'react';
import DataTable from 'react-data-table-component';
import { Button, Spinner } from 'react-bootstrap';
import { FaCode } from 'react-icons/fa';
import StatusBadge from './StatusBadge';
import { getDateHighlightColor } from './guestyMessageService';

/**
 * Component for displaying the message data table
 */
const MessageTable = ({
    filteredMessages,
    customStyles,
    isLoading,
    handleViewRawMessage,
    formatDateTime,
    customerMap,
    handleToggleStatus,
    isToggling,
    togglingId,
    dateHighlightMap
}) => {
    // Define table columns
    const tableColumns = [
        {
            name: 'ID',
            selector: row => row.id,
            sortable: true,
            cell: row => row.id,
            width: '80px',
        },
        {
            name: 'Customer',
            selector: row => customerMap[row.cosCustomerId] || row.cosCustomerId,
            sortable: true,
            cell: row => (
                <div>
                    <div>{customerMap[row.cosCustomerId] || 'Unknown'}</div>
                </div>
            ),
            width: '200px',
        },
        {
            name: 'Listing Address',
            selector: row => row.listingAddress,
            sortable: true,
            cell: row => row.listingAddress || 'N/A',
            style: {
                textAlign: 'left',
            },
        },
        {
            name: 'Check-In',
            selector: row => row.checkInTime,
            sortable: true,
            cell: row => {
                const highlightColor = getDateHighlightColor(row.id, 'checkIn', dateHighlightMap);
                return (
                    <div 
                        style={{ 
                            backgroundColor: highlightColor || 'transparent',
                            padding: '4px 8px',
                            borderRadius: '4px',
                            fontWeight: highlightColor ? 'bold' : 'normal'
                        }}
                    >
                        {formatDateTime(row.checkInTime)}
                    </div>
                );
            },
            width: '180px',
        },
        {
            name: 'Check-Out',
            selector: row => row.checkOutTime,
            sortable: true,
            cell: row => {
                const highlightColor = getDateHighlightColor(row.id, 'checkOut', dateHighlightMap);
                return (
                    <div 
                        style={{ 
                            backgroundColor: highlightColor || 'transparent',
                            padding: '4px 8px',
                            borderRadius: '4px',
                            fontWeight: highlightColor ? 'bold' : 'normal'
                        }}
                    >
                        {formatDateTime(row.checkOutTime)}
                    </div>
                );
            },
            width: '180px',
        },
        {
            name: 'Received Time',
            selector: row => row.receivedTime,
            sortable: true,
            cell: row => formatDateTime(row.receivedTime),
            width: '180px',
        },
        {
            name: 'Message Type',
            selector: row => row.messageType,
            sortable: true,
            cell: row => row.messageType || 'N/A',
            width: '120px',
        },
        {
            name: 'Reservation ID',
            selector: row => row.reservationId,
            sortable: true,
            cell: row => row.reservationId || 'N/A',
            width: '200px',
        },
        {
            name: 'Job ID',
            selector: row => row.cosJobId,
            sortable: true,
            cell: row => {
                if (row.cosJobId) {
                    return (
                        <a href={`/manage-job/update-job/${row.cosJobId}`} target="_blank" rel="noopener noreferrer">
                            {row.cosJobId}
                        </a>
                    );
                }
                return 'N/A';
            },
            width: '100px',
        },
        {
            name: 'Property ID',
            selector: row => row.propertyId,
            sortable: true,
            cell: row => row.propertyId || 'N/A',
            width: '100px',
        },
        {
            name: 'Status',
            selector: row => row.processingStatus,
            sortable: true,
            cell: row => (
                <StatusBadge 
                    status={row.processingStatus}
                    onToggle={handleToggleStatus}
                    isToggling={isToggling}
                    togglingId={togglingId}
                    id={row.id}
                />
            ),
            width: '150px',
        },
        {
            name: 'Message',
            selector: row => row.parsingError,
            sortable: true,
            cell: row => row.parsingError || 'N/A',
            style: {
                textAlign: 'left',
            },
            wrap: true,
        },
        {
            name: 'Raw Message',
            button: "true",
            cell: row => (
                <Button 
                    variant="outline-info" 
                    size="sm" 
                    onClick={() => handleViewRawMessage(row.id)}
                >
                    <FaCode className="me-1" /> View
                </Button>
            ),
            width: '120px',
        },
    ];

    return (
        <DataTable
            columns={tableColumns}
            data={filteredMessages}
            customStyles={customStyles}
            pagination
            highlightOnHover
            responsive
            striped
            progressPending={isLoading && !filteredMessages.length}
            progressComponent={<Spinner animation="border" variant="primary" />}
            noDataComponent={
                <div className="p-4 text-center">
                    No messages found matching your criteria
                </div>
            }
        />
    );
};

export default MessageTable;
